import React from 'react'
import { Button } from '@reapit/elements/core/button'
import { AdminFormField } from '../../../../api/types'
import {
  FieldConditionBadge,
  FieldPreviewAdorned,
  FieldPreviewAdornedInput,
  FieldPreviewAdornment,
  FieldPreviewControl,
  FieldPreviewHelp,
  FieldPreviewLabel,
  FieldPreviewRequired,
  FieldPreviewTextArea,
  PreviewCheckboxList,
  PreviewOptionRow,
  PreviewRadioList,
  PreviewRadioMark,
  PreviewSelectBox,
} from '../../style'
import { AuthoredFieldBox, FieldActionsRow, PreviewCheckboxMark, PreviewRadioMarkChecked } from './style'

interface FieldPreviewProps {
  field: AdminFormField
  /** For composite fields (Select / RadioGroup / boolean): the option child fields, ordered. */
  options?: AdminFormField[]
  /** When non-empty, this field is only shown when ANY of these conditions match — rendered as a badge. */
  conditions?: { controllingLabel: string; optionLabel: string }[]
  onEdit: () => void
  onRemove: () => void
  /** Hides the Edit/Delete row — set when the environment is read-only (see AccessModeProvider). */
  readOnly?: boolean
}

/** A validation type is "required" when its name mentions it (e.g. Required, RequiredDateTime). */
const isRequired = (validation: string | null): boolean =>
  Boolean(validation) && validation!.toLowerCase().includes('required')

/** Clamp the field's editWidth (%) into a flex-basis; null/invalid falls back to full width. */
const flexBasis = (editWidth: number | null): string => {
  const width = editWidth && editWidth > 0 && editWidth <= 100 ? editWidth : 100
  // Subtract half the grid gap so two 50% fields sit on one row alongside the 1rem gap.
  return `calc(${width}% - 0.5rem)`
}

/**
 * Renders a single form field the way the agent sees it in the edit view: label above a read-only
 * control matching the field's editType. Hovering reveals an Edit link. Purely presentational — the
 * controls are non-interactive (this is a builder preview, not a live form).
 */
export const FieldPreview: React.FC<FieldPreviewProps> = ({
  field,
  options = [],
  conditions = [],
  onEdit,
  onRemove,
  readOnly = false,
}) => {
  // Only the display name is shown as a label — no fallback, so a field with no name renders bare.
  const label = field.editDisplayName ?? ''
  const placeholder = field.editPlaceholder ?? ''
  const value = field.defaultData ?? ''
  const controlId = `field-preview-${field.referenceID}`

  const renderControl = () => {
    switch (field.editType) {
      case 'Select':
        return (
          <PreviewSelectBox>
            {placeholder || 'Select…'}
            <span aria-hidden>▾</span>
          </PreviewSelectBox>
        )
      case 'RadioGroup':
        return (
          <PreviewRadioList>
            {options
              .filter((option) => option.editDisplayName || option.defaultData)
              .map((option) => {
                const checked = Boolean(field.defaultData) && option.defaultData === field.defaultData
                return (
                  <PreviewOptionRow key={option.referenceID}>
                    {checked ? <PreviewRadioMarkChecked /> : <PreviewRadioMark />}
                    {option.editDisplayName || option.defaultData}
                  </PreviewOptionRow>
                )
              })}
          </PreviewRadioList>
        )
      case 'CheckboxGroup':
        // Unlike RadioGroup/Select, a Checkbox option's defaultData holds the optional long-form PDF merge
        // text (see field-drawer's "Write to PDF" feature), not a short identifying value - falling back to
        // it here would render the whole merge-text paragraph as the option's preview label. jsonPropertyName
        // is the option's real short key and always set (resolveCheckboxKey), so fall back to that instead.
        return (
          <PreviewCheckboxList>
            {options
              .filter((option) => option.editDisplayName || option.jsonPropertyName)
              .map((option) => (
                <PreviewOptionRow key={option.referenceID}>
                  <PreviewCheckboxMark />
                  {option.editDisplayName || option.jsonPropertyName}
                </PreviewOptionRow>
              ))}
          </PreviewCheckboxList>
        )
      case 'TextArea':
        return (
          <FieldPreviewTextArea id={controlId} rows={3} readOnly disabled placeholder={placeholder} value={value} />
        )
      case 'Currency':
        return (
          <FieldPreviewAdorned>
            <FieldPreviewAdornment>{field.displayPrefix || '$'}</FieldPreviewAdornment>
            <FieldPreviewAdornedInput
              id={controlId}
              readOnly
              disabled
              inputMode="decimal"
              placeholder={placeholder}
              value={value}
            />
          </FieldPreviewAdorned>
        )
      case 'Date':
        return (
          <FieldPreviewControl
            id={controlId}
            readOnly
            disabled
            placeholder={placeholder || 'dd/mm/yyyy'}
            value={value}
          />
        )
      case 'Number':
        return (
          <FieldPreviewControl
            id={controlId}
            readOnly
            disabled
            inputMode="numeric"
            placeholder={placeholder}
            value={value}
          />
        )
      case 'None':
        return <FieldPreviewHelp>Hidden field — stored, not shown to the agent.</FieldPreviewHelp>
      case 'Text':
      default:
        return <FieldPreviewControl id={controlId} readOnly disabled placeholder={placeholder} value={value} />
    }
  }

  return (
    <AuthoredFieldBox style={{ flex: `0 0 ${flexBasis(field.editWidth)}` }}>
      {conditions.length > 0 && (
        <FieldConditionBadge
          title={`Only shows when ${conditions
            .map((condition) => `${condition.controllingLabel} = ${condition.optionLabel}`)
            .join(' OR ')}`}
        >
          Conditional visibility
        </FieldConditionBadge>
      )}
      {label && (
        <FieldPreviewLabel htmlFor={controlId}>
          {label}
          {isRequired(field.validation) && <FieldPreviewRequired>*</FieldPreviewRequired>}
        </FieldPreviewLabel>
      )}
      {renderControl()}
      {field.editDisplayDescription && <FieldPreviewHelp>{field.editDisplayDescription}</FieldPreviewHelp>}
      {!readOnly && (
        <FieldActionsRow>
          <Button variant="tertiary" useLinkStyle size="small" type="button" onClick={onEdit}>
            Edit
          </Button>
          <Button variant="tertiary" useLinkStyle isDestructive size="small" type="button" onClick={onRemove}>
            Delete
          </Button>
        </FieldActionsRow>
      )}
    </AuthoredFieldBox>
  )
}
